import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Mail, Phone, MessageSquare, Globe, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import useBulkSelection from "@/hooks/useBulkSelection";

const TAG_TONE = { new: "blue", repeat: "green", blacklist: "red" };
const STATUS_TONE = { invited: "blue", submitted: "yellow", changes_requested: "orange", active: "green", suspended: "red" };

// Phase 84.6 — CRM tracking values (mirror backend CLIENT_HEALTH_VALUES / CLIENT_ENGAGEMENT_VALUES)
const HEALTH_OPTIONS = ["Active", "At Risk", "Churned", "Dormant"];
const ENGAGEMENT_OPTIONS = ["Onboarding", "Steady", "Expansion", "Renewal Due", "Disengaged"];
const HEALTH_TONE = { "Active": "green", "At Risk": "yellow", "Churned": "red", "Dormant": "slate" };
const ENGAGEMENT_TONE = { "Onboarding": "blue", "Steady": "green", "Expansion": "purple", "Renewal Due": "orange", "Disengaged": "slate" };

// Currency helper for contract value.
const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(1)}Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)}L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(0)}K`;
  return `₹${num}`;
};

// Each client gets a deterministic colour for its initials avatar
const AVATAR_PALETTE = ["#F54800", "#0F172A", "#16A34A", "#2563EB", "#9333EA", "#DB2777", "#0D9488", "#B45309"];
function avatarColor(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}
function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
}

const emptyClientForm = () => ({
  email: "", name: "",
  company: "", title: "", project_id: "", tag: "new",
  website: "", phone: "", poc_name: "", poc_email: "", poc_phone: "", notes: "",
  // Phase 84.6 — CRM tracking fields
  health: "", engagement: "", contract_value: "", renewal_date: "", csm_owner_id: "",
});

export default function AdminClients({ embedded = false }) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);  // for CSM owner picker
  const [contracts, setContracts] = useState([]);          // Phase 84.8 — for "N contracts" badge on client cards
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClientForm);
  const [statusMenu, setStatusMenu] = useState(null); // client id whose status menu is open

  const load = async () => {
    const [c, p, u, k] = await Promise.all([
      api.get("/admin/clients"),
      api.get("/projects"),
      api.get("/users?kind=internal").catch(() => ({ data: [] })),
      api.get("/contracts").catch(() => ({ data: [] })),
    ]);
    setClients(c.data); setProjects(p.data); setInternalUsers(u.data || []); setContracts(k.data || []);
  };
  useEffect(() => { load(); }, []);

  const bulk = useBulkSelection({
    collection: "users_client",
    ids: clients.map((c) => c.id),
    onDone: load,
  });

  // Open create dialog when "?new=1" set by global "+ Create" menu
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(null); setForm(emptyClientForm()); setOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const unassignedProjects = useMemo(() => projects.filter((p) => !p.client_user_id), [projects]);
  const linkedByClient = useMemo(() => {
    const m = {};
    for (const p of projects) {
      if (!p.client_user_id) continue;
      (m[p.client_user_id] = m[p.client_user_id] || []).push(p);
    }
    return m;
  }, [projects]);
  // Phase 84.8 — contract rollup per client (total + signed count).
  const contractsByClient = useMemo(() => {
    const m = {};
    for (const c of contracts) {
      if (!c.client_user_id) continue;
      const rec = m[c.client_user_id] || (m[c.client_user_id] = { total: 0, signed: 0 });
      rec.total += 1;
      const s = (c.status || "").toLowerCase();
      // Code-review fix (iter79) — statuses actually emitted by the contracts
      // API are {draft, sent, approved, signed, completed}. Anything in the
      // "signed or later" bucket counts as signed for the rollup.
      if (s === "signed" || s === "approved" || s === "completed") rec.signed += 1;
    }
    return m;
  }, [contracts]);

  const startCreate = () => { setEditing(null); setForm(emptyClientForm()); setOpen(true); };
  const startEdit = (c) => {
    const meta = c.crm_meta || {};
    setEditing(c);
    setForm({
      email: c.email, name: c.name, company: c.company || "", title: c.title || "",
      project_id: "", tag: c.tag || "new",
      website: c.website || "", phone: c.phone || "",
      poc_name: c.poc_name || "", poc_email: c.poc_email || "", poc_phone: c.poc_phone || "",
      notes: c.notes || "",
      // Phase 84.6 — hydrate CRM fields from crm_meta
      health: meta.health || "",
      engagement: meta.engagement || "",
      contract_value: meta.contract_value ?? "",
      renewal_date: meta.renewal_date || "",
      csm_owner_id: meta.csm_owner_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      // Base client fields + CRM tracking fields. Empty strings sent as null
      // so the backend clears the crm_meta.* entry rather than persisting "".
      const editable = {
        name: form.name, company: form.company, title: form.title, tag: form.tag,
        website: form.website, phone: form.phone,
        poc_name: form.poc_name, poc_email: form.poc_email, poc_phone: form.poc_phone, notes: form.notes,
        health: form.health || null,
        engagement: form.engagement || null,
        contract_value: form.contract_value === "" || form.contract_value === null ? null : Number(form.contract_value),
        renewal_date: form.renewal_date || null,
        csm_owner_id: form.csm_owner_id || null,
      };
      if (editing) {
        await api.patch(`/admin/clients/${editing.id}`, editable);
        toast.success("Client updated");
      } else {
        const { data } = await api.post("/admin/clients", { ...editable, email: form.email, project_id: form.project_id || null });
        toast.success(`Invite sent to ${data.email} — they'll set their own password.`);
      }
      setOpen(false); setEditing(null); setForm(emptyClientForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? Their portal access is revoked and projects unlink.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // status here is the onboarding lifecycle (suspended / active toggle)
  const setStatus = async (c, status) => {
    try {
      if (status === "suspended") await api.post(`/admin/parties/${c.id}/suspend`);
      else if (status === "active") await api.post(`/admin/parties/${c.id}/reactivate`);
      toast.success(`Marked ${status}`);
      setStatusMenu(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const goToChat = (c) => {
    const linked = linkedByClient[c.id] || [];
    if (linked.length === 0) {
      toast.info("No project linked yet — link one to chat.");
      return;
    }
    // Feb 2026 — Information Cleanse: project chat now lives in each
    // project's Sidecar drawer, not the global Chat tab.
    nav(`/projects/${linked[0].id}`);
  };

  return (
    <div className={embedded ? "fade-in" : "p-4 sm:p-8 max-w-6xl mx-auto fade-in"} data-testid="admin-clients-page">
      {embedded ? (
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4" data-testid="admin-clients-embedded-actions">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">Every client account, with their POC, website, projects and direct contact channels.</div>
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            <BulkUploadDialog section="clients" label="Clients" onCommitted={load} />
            <Button onClick={startCreate} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-client-btn"><Plus size={14} className="mr-1.5" />Add client</Button>
          </div>
        </div>
      ) : (
        <PageHeader
          eyebrow="Relationships"
          title="Clients"
          subtitle="Every client account, with their POC, website, projects and direct contact channels."
          actions={
            <div className="inline-flex items-center gap-2 flex-wrap justify-end">
              <BulkUploadDialog section="clients" label="Clients" onCommitted={load} />
              <Button onClick={startCreate} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-client-btn"><Plus size={14} className="mr-1.5" />Add client</Button>
            </div>
          }
        />
      )}

      {clients.length === 0 ? (
        <div className="card-flat p-8 text-center text-sm text-slate-500" data-testid="no-clients">
          No clients onboarded yet.
        </div>
      ) : (
        <>
        {bulk.canBulk && (
          <div
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-2 py-1 border border-slate-200 rounded-sm bg-white mb-3 cursor-pointer"
            data-testid="clients-select-all-label"
            onClick={() => bulk.toggleAll()}
          >
            <bulk.HeaderCheckbox /> <span>Select all</span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((c) => {
            const linked = linkedByClient[c.id] || [];
            const tag = c.tag || "new";
            const status = c.status || "active";
            const color = avatarColor(c.email || c.name);
            return (
              <div key={c.id} className={`card-flat p-4 flex flex-col group relative ${bulk.isSelected(c.id) ? "ring-2 ring-[#F54800]" : ""}`} data-testid={`client-card-${c.id}`}>
                {bulk.canBulk && (
                  <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
                    <bulk.RowCheckbox id={c.id} />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0 font-display font-bold text-white text-lg" style={{ background: color }} data-testid={`client-avatar-${c.id}`}>
                    {initials(c.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-display font-bold text-slate-900 truncate">{c.name}</div>
                      <Tag tone={TAG_TONE[tag] || "slate"}>{tag}</Tag>
                    </div>
                    {c.company && <div className="text-xs text-slate-500 truncate">{c.company}</div>}
                    {c.title && <div className="text-[11px] text-slate-400 truncate">{c.title}</div>}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <button onClick={() => startEdit(c)} className="p-1.5 hover:bg-slate-100 rounded-sm" title="Edit" data-testid={`edit-client-${c.id}`}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => deleteClient(c)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" title="Delete" data-testid={`delete-client-${c.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <div className="truncate"><span className="text-slate-400">Email:</span> {c.email}</div>
                  {c.phone && <div className="truncate"><span className="text-slate-400">Phone:</span> {c.phone}</div>}
                  {c.website && (
                    <div className="truncate"><span className="text-slate-400">Web:</span>{" "}
                      <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer noopener" className="text-[#F54800] hover:underline inline-flex items-center gap-0.5">
                        <Globe size={9} /> {c.website.replace(/^https?:\/\//, "")}
                      </a>
                    </div>
                  )}
                  {c.poc_name && (
                    <div className="border-t border-slate-100 pt-1 mt-1">
                      <span className="text-slate-400">POC:</span> {c.poc_name}
                      {c.poc_email && <span className="text-slate-400"> · {c.poc_email}</span>}
                    </div>
                  )}
                  <div className="truncate"><span className="text-slate-400">Projects:</span> {linked.length ? linked.map((p) => p.name).join(", ") : "—"}</div>
                </div>

                {/* Phase 84.6 — CRM tracking summary. Only renders if any crm_meta field is set OR contracts exist. */}
                {(() => {
                  const m = c.crm_meta || {};
                  const contractStats = contractsByClient[c.id];
                  const hasAny = m.health || m.engagement || m.contract_value != null || m.renewal_date || m.csm_owner_id || contractStats;
                  if (!hasAny) return null;
                  const csm = internalUsers.find((u) => u.id === m.csm_owner_id);
                  return (
                    <div className="mt-3 pt-2 border-t border-slate-100 space-y-1 text-[11px]" data-testid={`client-crm-meta-${c.id}`}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {m.health && <Tag tone={HEALTH_TONE[m.health] || "slate"} data-testid={`client-health-${c.id}`}>{m.health}</Tag>}
                        {m.engagement && <Tag tone={ENGAGEMENT_TONE[m.engagement] || "slate"} data-testid={`client-engagement-${c.id}`}>{m.engagement}</Tag>}
                        {contractStats && (
                          <a href="/crm?tab=contracts" className="inline-block" data-testid={`client-contracts-chip-${c.id}`}>
                            <Tag tone="purple">Contracts · {contractStats.signed}/{contractStats.total}</Tag>
                          </a>
                        )}
                      </div>
                      {(m.contract_value != null || m.renewal_date) && (
                        <div className="text-slate-600 flex items-center gap-3 flex-wrap">
                          {m.contract_value != null && <span data-testid={`client-contract-value-${c.id}`}><span className="text-slate-400">Value:</span> {fmtMoney(m.contract_value)}</span>}
                          {m.renewal_date && <span data-testid={`client-renewal-${c.id}`}><span className="text-slate-400">Renews:</span> {m.renewal_date}</span>}
                        </div>
                      )}
                      {csm && <div className="text-slate-500 truncate" data-testid={`client-csm-${c.id}`}><span className="text-slate-400">CSM:</span> {csm.name}</div>}
                    </div>
                  );
                })()}

                <div className="mt-3 flex items-center gap-1 flex-wrap">
                  <a
                    href={`mailto:${c.email}`}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700"
                    title="Email"
                    data-testid={`client-email-btn-${c.id}`}
                  >
                    <Mail size={11} /> Email
                  </a>
                  <a
                    href={c.phone ? `tel:${c.phone.replace(/\s+/g, "")}` : "#"}
                    onClick={(e) => { if (!c.phone) { e.preventDefault(); toast.info("Add a phone number to call."); } }}
                    className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-slate-200 text-slate-700 ${c.phone ? "hover:bg-slate-50 hover:border-slate-300" : "opacity-60"}`}
                    title="Call"
                    data-testid={`client-call-btn-${c.id}`}
                  >
                    <Phone size={11} /> Call
                  </a>
                  <button
                    type="button"
                    onClick={() => goToChat(c)}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700"
                    title="Chat"
                    data-testid={`client-chat-btn-${c.id}`}
                  >
                    <MessageSquare size={11} /> Chat
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setStatusMenu(statusMenu === c.id ? null : c.id)}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700"
                      title="Set status"
                      data-testid={`client-status-btn-${c.id}`}
                    >
                      <Tag tone={STATUS_TONE[status] || "slate"}>{status}</Tag>
                      <MoreVertical size={10} />
                    </button>
                    {statusMenu === c.id && (
                      <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[120px]" data-testid={`client-status-menu-${c.id}`}>
                        {["active", "suspended"].map((s) => (
                          <button
                            key={s}
                            onClick={() => setStatus(c, s)}
                            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${status === s ? "font-bold text-slate-900" : "text-slate-700"}`}
                            data-testid={`client-status-set-${c.id}-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit client" : "Onboard client"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Name</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
              <div><Label className="label-overline">Company</Label><Input className="rounded-sm mt-1" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            </div>
            {!editing && <div><Label className="label-overline">Email</Label><Input className="rounded-sm mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>}
            {!editing && (
              <div className="bg-[#F54800]/5 border border-[#F54800]/20 rounded-sm p-2.5 text-[11px] text-slate-700" data-testid="client-magic-link-notice">
                A magic-link invitation will be emailed to <b>{form.email || "this address"}</b>. They&apos;ll set their own password and complete their profile — you&apos;ll approve them before they can sign in.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" placeholder="e.g. Founder & CEO" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="label-overline">Website</Label><Input className="rounded-sm mt-1" placeholder="https://acme.co" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="client-website" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Phone</Label><Input className="rounded-sm mt-1" placeholder="+91…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" /></div>
              <div>
                <Label className="label-overline">Tag</Label>
                <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="client-tag"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="repeat">Repeat</SelectItem>
                    <SelectItem value="blacklist">Blacklist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="label-overline mb-2">Primary point of contact (POC)</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-[10px] uppercase tracking-wider">Name</Label><Input className="rounded-sm mt-1" value={form.poc_name} onChange={(e) => setForm({ ...form, poc_name: e.target.value })} data-testid="client-poc-name" /></div>
                <div><Label className="text-[10px] uppercase tracking-wider">Email</Label><Input className="rounded-sm mt-1" value={form.poc_email} onChange={(e) => setForm({ ...form, poc_email: e.target.value })} data-testid="client-poc-email" /></div>
                <div><Label className="text-[10px] uppercase tracking-wider">Phone</Label><Input className="rounded-sm mt-1" value={form.poc_phone} onChange={(e) => setForm({ ...form, poc_phone: e.target.value })} data-testid="client-poc-phone" /></div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">After saving, use the <b>POCs</b> panel below to add multiple contacts (Name / Designation / Email / Phone).</p>
            </div>

            {editing && (
              <ClientPocsPanel clientId={editing.id} />
            )}

            <div>
              <Label className="label-overline">Notes / additional contacts</Label>
              <Textarea className="rounded-sm mt-1" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Secondary contacts, billing address, account peculiarities…" data-testid="client-notes" />
            </div>

            {/* Phase 84.6 — CRM tracking fields (persisted under crm_meta.*) */}
            <div className="border-t border-slate-200 pt-3 mt-2" data-testid="client-crm-section">
              <div className="label-overline mb-2">CRM tracking</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Health</Label>
                  <Select value={form.health || "none"} onValueChange={(v) => setForm({ ...form, health: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="client-health"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— (unset)</SelectItem>
                      {HEALTH_OPTIONS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-overline">Engagement</Label>
                  <Select value={form.engagement || "none"} onValueChange={(v) => setForm({ ...form, engagement: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="client-engagement"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— (unset)</SelectItem>
                      {ENGAGEMENT_OPTIONS.map((e2) => <SelectItem key={e2} value={e2}>{e2}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="label-overline">Contract value (₹)</Label>
                  <Input
                    className="rounded-sm mt-1"
                    type="number"
                    inputMode="decimal"
                    placeholder="e.g. 500000"
                    value={form.contract_value}
                    onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
                    data-testid="client-contract-value"
                  />
                </div>
                <div>
                  <Label className="label-overline">Renewal date</Label>
                  <Input
                    className="rounded-sm mt-1"
                    type="date"
                    value={form.renewal_date}
                    onChange={(e) => setForm({ ...form, renewal_date: e.target.value })}
                    data-testid="client-renewal-date"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="label-overline">CSM owner</Label>
                <Select value={form.csm_owner_id || "none"} onValueChange={(v) => setForm({ ...form, csm_owner_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="client-csm-owner"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— (unassigned)</SelectItem>
                    {internalUsers
                      .filter((u) => ["admin", "founder", "client_manager"].includes(u.role))
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!editing && (
              <div>
                <Label className="label-overline">Link to project</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="client-project"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— (no project yet)</SelectItem>
                    {unassignedProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.business_unit}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="save-client">{editing ? "Save" : "Onboard"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </div>
  );
}


/* ============================================================================
 * <ClientPocsPanel /> — multi-POC manager embedded in the Edit Client dialog.
 * ==========================================================================*/
function ClientPocsPanel({ clientId }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ name: "", designation: "", email: "", phone: "", is_primary: false });
  const [editId, setEditId] = useState(null);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await api.get(`/admin/clients/${clientId}/pocs`); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.name?.trim()) { toast.error("POC name is required"); return; }
    try {
      await api.post(`/admin/clients/${clientId}/pocs`, draft);
      setDraft({ name: "", designation: "", email: "", phone: "", is_primary: false });
      load(); toast.success("POC added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async (r) => {
    try { await api.patch(`/admin/clients/${clientId}/pocs/${r.id}`, r); setEditId(null); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name}?`)) return;
    try { await api.delete(`/admin/clients/${clientId}/pocs/${r.id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border-t border-slate-200 pt-3 mt-2" data-testid="client-pocs-panel">
      <div className="label-overline mb-2">Points of Contact · {rows.length}</div>
      <div className="space-y-1.5 mb-3">
        {rows.length === 0 && <div className="text-[11px] text-slate-400 italic">No additional POCs yet.</div>}
        {rows.map((r) => editId === r.id ? (
          <div key={r.id} className="grid grid-cols-12 gap-2 items-center bg-orange-50/40 border border-orange-200 rounded-sm p-2" data-testid={`poc-edit-${r.id}`}>
            <Input className="rounded-sm h-8 text-xs col-span-3" value={r.name || ""} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, name: e.target.value } : x))} placeholder="Name" />
            <Input className="rounded-sm h-8 text-xs col-span-3" value={r.designation || ""} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, designation: e.target.value } : x))} placeholder="Designation" />
            <Input className="rounded-sm h-8 text-xs col-span-3" value={r.email || ""} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, email: e.target.value } : x))} placeholder="Email" />
            <Input className="rounded-sm h-8 text-xs col-span-2" value={r.phone || ""} onChange={(e) => setRows(rows.map((x) => x.id === r.id ? { ...x, phone: e.target.value } : x))} placeholder="Phone" />
            <div className="col-span-1 flex gap-1 justify-end">
              <button onClick={() => saveEdit(r)} className="text-xs text-green-700 hover:underline" data-testid={`poc-save-${r.id}`}>Save</button>
            </div>
          </div>
        ) : (
          <div key={r.id} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border border-slate-200 rounded-sm bg-white text-xs" data-testid={`poc-row-${r.id}`}>
            <div className="col-span-3 font-medium text-slate-800 truncate">{r.name}{r.is_primary && <span className="ml-1 text-[9px] uppercase tracking-wider text-[#F54800]">· primary</span>}</div>
            <div className="col-span-3 text-slate-500 truncate">{r.designation || "—"}</div>
            <div className="col-span-3 text-slate-700 truncate">{r.email || "—"}</div>
            <div className="col-span-2 text-slate-500 truncate font-mono text-[11px]">{r.phone || "—"}</div>
            <div className="col-span-1 flex gap-1 justify-end">
              <button onClick={() => setEditId(r.id)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`poc-edit-btn-${r.id}`}><Pencil size={11} /></button>
              <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`poc-delete-${r.id}`}><Trash2 size={11} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-2 border border-slate-200 rounded-sm p-2 bg-slate-50/60">
        <Input className="rounded-sm h-8 text-xs col-span-3" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name *" data-testid="poc-add-name" />
        <Input className="rounded-sm h-8 text-xs col-span-3" value={draft.designation} onChange={(e) => setDraft({ ...draft, designation: e.target.value })} placeholder="Designation" />
        <Input className="rounded-sm h-8 text-xs col-span-3" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Email" data-testid="poc-add-email" />
        <Input className="rounded-sm h-8 text-xs col-span-2" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="Phone" />
        <Button onClick={add} size="sm" className="col-span-1 rounded-sm h-8 bg-slate-900 hover:bg-slate-800 text-white text-xs px-2" data-testid="poc-add-btn"><Plus size={11} /></Button>
      </div>
    </div>
  );
}
